import { queryOptions } from "@tanstack/react-query";

import {
	apiPaths,
	paymentDocumentAnswerSchema,
	paymentDocumentDetailSchema,
	paymentDocumentPath,
	paymentDocumentSettlementPath,
	paymentsPageSchema,
	paymentsSummarySchema,
	type PaymentDocument,
	type PaymentSide,
	type PaymentStatus,
	type SettlementRequest,
} from "@ravnar/api-contract";

import { ApiError, apiRequest } from "./client";
import { LIST_STALE_TIME_MS } from "./refresh";

// The requests of Finance's payables and receivables. Every query key starts with "payments", so settling a document,
// or reopening one, refreshes the figures and every page at once; a transaction matched on the Reconciliation page,
// which pays a document, asks them again too.

/** Both sides of the current year in figures, as Finance's overview shows them; anyone who can read Finance. */
export const paymentsSummaryQueryOptions = () =>
	queryOptions({
		queryKey: ["payments", "summary"],
		queryFn: ({ signal }) => apiRequest(apiPaths.payments, paymentsSummarySchema, { signal }),
		staleTime: LIST_STALE_TIME_MS,
	});

export interface PaymentsPageRequest {
	/** Only the documents of this status; every status when there is none. */
	readonly status?: PaymentStatus;
	/** A page link from an earlier page; the first page when there is none. */
	readonly page?: string;
}

/** The path each side's pages are read from. */
const SIDE_PATHS: Readonly<Record<PaymentSide, string>> = {
	payables: apiPaths.payables,
	receivables: apiPaths.receivables,
};

/** A page of one side's documents of the year, with every status counted; anyone who can read Finance. */
export const paymentsPageQueryOptions = (side: PaymentSide, { status, page }: PaymentsPageRequest = {}) =>
	queryOptions({
		queryKey: ["payments", side, { status, page }],
		queryFn: ({ signal }) =>
			apiRequest(SIDE_PATHS[side], paymentsPageSchema, {
				query: { ...(status !== undefined && { status }), ...(page !== undefined && { page }) },
				signal,
			}),
		staleTime: LIST_STALE_TIME_MS,
		// A refused page link will not change on a retry, and the page leaves it for the first page straight away.
		retry: (failureCount, error) => failureCount < 1 && !(error instanceof ApiError && error.status < 500),
	});

/**
 * One document of the year and every transaction matched to it; anyone who can read Finance. A document no page
 * follows answers 404, which no retry changes.
 */
export const paymentDocumentQueryOptions = (fileId: string) =>
	queryOptions({
		queryKey: ["payments", "document", fileId],
		queryFn: ({ signal }) => apiRequest(paymentDocumentPath(fileId), paymentDocumentDetailSchema, { signal }),
		staleTime: LIST_STALE_TIME_MS,
		retry: (failureCount, error) => failureCount < 1 && !(error instanceof ApiError && error.status < 500),
	});

/** Settles a document by hand, why and with what note; answers it as it now stands. An editor of Finance. */
export async function settlePaymentDocument(fileId: string, request: SettlementRequest): Promise<PaymentDocument> {
	const { document } = await apiRequest(paymentDocumentSettlementPath(fileId), paymentDocumentAnswerSchema, {
		method: "PUT",
		body: request,
	});
	return document;
}

/** Takes back what settles a document by hand, and answers it as it now stands. An editor of Finance. */
export async function reopenPaymentDocument(fileId: string): Promise<PaymentDocument> {
	const { document } = await apiRequest(paymentDocumentSettlementPath(fileId), paymentDocumentAnswerSchema, {
		method: "DELETE",
	});
	return document;
}
